import React, { useEffect, useState } from 'react';
import { ProjectControlsComponent } from 'src/pages/ControlDeskPage/ProjectControls/ProjectControlsComponent';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { ProjectTitle } from 'src/pages/ControlDeskPage/ProjectTitle/ProjectTitle';
import { IntegrationStatusSection } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationStatusSection';
import { DataSourceSection } from 'src/pages/ControlDeskPage/DataSourceSection/DataSourceSection';
import { clsx } from 'clsx';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { useTheme } from 'src/provider/ThemeProvider';
import { ProductsTypeColor } from 'src/constants/products';
import { ConnectYouAIAgentBanner } from 'src/pages/ControlDeskPage/ConnectYouAIAgentBanner/ConnectYouAIAgentBanner';
import { VerifyYourEmailBanner } from 'src/pages/ControlDeskPage/VerifyYourEmailBanner/VerifyYourEmailBanner';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { RepliesBalanceSection } from 'src/pages/ControlDeskPage/RepliesBalanceSection/RepliesBalanceSection';
import { TrialBanner } from 'src/pages/ControlDeskPage/TrialBanner/TrialBanner';
import { PlaygroundSection } from 'src/pages/ControlDeskPage/PlaygroundSection/PlaygroundSection';
import { AiConfigurationSection } from 'src/pages/ControlDeskPage/AiConfigurationSection/AiConfigurationSection';
import { useGetProjectByIdQuery } from 'src/services';
import { WeeklyAnalyticsSection } from 'src/pages/ControlDeskPage/WeeklyAnalyticsSection/WeeklyAnalyticsSection';
import { IntegrationFinalPopup } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationFinalPopup/IntegrationFinalPopup';
import { NewProjectFinalPopup } from 'src/pages/ControlDeskPage/NewProjectFinalPopup/NewProjectFinalPopup';
import { useGetAccountsQuery } from 'src/services/accountsService';

const ControlDeskPage = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isFullScreenConfiguration, setIsFullScreenConfiguration] = useState(false);
  const fullScreenBtnClickHandler = () => {
    setIsFullScreen((prev) => !prev);
  };
  const fullScreenConfigurationBtnClickHandler = () => {
    setIsFullScreenConfiguration((prev) => !prev);
  };
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const { setTheme } = useTheme();
  const { data: userInfo } = useGetUsersMeQuery();
  const { data: account } = useGetAccountsQuery();
  const { isTrialPlan, isInactivePlan } = useAppSelector((s) => s.planSlice);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const isEmpty = !Boolean(Object.keys(account?.general_plan || {})?.length);

  useEffect(() => {
    if (projectTypeVar) {
      setTheme(ProductsTypeColor[projectTypeVar]);
    }
  }, []);

  const is = {
    emailVerified: Boolean(userInfo && userInfo.is_active),
    integration: Boolean(currentProject?.integrations?.length),
  };

  return (
    <PageContentWrapper
      projectBreadcrumbsContent={'Control Desk'}
      className="project-page-container h-[calc(100%-70px)] !pb-5 relative !pt-[15px]"
      isMobileMarginTop={false}
    >
      <div className="flex items-center gap-3 justify-between mb-[5px]">
        <ProjectTitle />
        <ProjectControlsComponent />
      </div>
      {!is.emailVerified && <VerifyYourEmailBanner />}
      {is.emailVerified && !isInactivePlan && !is.integration && <ConnectYouAIAgentBanner />}
      {is.emailVerified && isTrialPlan && (is.integration || isInactivePlan) && <TrialBanner />}
      <div className="mx-auto w-full h-full overflow-auto">
        <div
          className={clsx(
            'gap-2.5 h-full transition-all grid grid-cols-12 grid-rows-[auto_auto_auto]',
          )}
        >
          {((isTrialPlan && !isInactivePlan && !is.integration) || !isTrialPlan) && (
            <div
              className={clsx(
                (isFullScreenConfiguration || isFullScreen) && 'hidden',
                'col-span-7 row-span-1 rounded-sm-5',
              )}
            >
              <RepliesBalanceSection isTrialPlan={isTrialPlan} />
            </div>
          )}
          <div
            className={clsx(
              (isFullScreenConfiguration || isFullScreen) && 'hidden',
              'col-span-7 row-span-3 rounded-sm-5 min-h-[400px]',
            )}
          >
            <WeeklyAnalyticsSection />
          </div>
          <div
            className={clsx(
              isFullScreenConfiguration && 'hidden',
              'transition-all duration-300',
              isFullScreen
                ? 'absolute w-full h-full top-0 left-0 col-span-12 row-span-12 row-start-1 col-start-1 z-30'
                : `col-span-5 row-start-1 col-start-8 max-h-[700px]`,
              isTrialPlan || isEmpty || !is.integration ? 'row-span-5' : 'row-span-4',
            )}
          >
            <PlaygroundSection
              isFullScreen={isFullScreen}
              fullScreenBtnClickHandler={fullScreenBtnClickHandler}
            />
          </div>

          <div
            className={clsx(
              isFullScreenConfiguration
                ? 'absolute z-30 w-full h-full top-0 left-0 col-span-12 row-start-1 col-start-1 row-span-12'
                : 'col-span-7',
              isFullScreen && 'hidden',
              'rounded-sm-5',
            )}
          >
            <AiConfigurationSection
              isFullScreenConfiguration={isFullScreenConfiguration}
              fullScreenConfigurationBtnClickHandler={fullScreenConfigurationBtnClickHandler}
            />
          </div>
          {Boolean(currentProject?.integrations?.length) && (
            <div
              className={clsx(
                (isFullScreenConfiguration || isFullScreen) && 'hidden',
                'rounded-sm-5',
                isTrialPlan ? 'col-span-7' : 'col-span-5',
              )}
            >
              <IntegrationStatusSection />
            </div>
          )}
          <div
            className={clsx(
              (isFullScreenConfiguration || isFullScreen) && 'hidden',
              'col-span-12 rounded-sm-5 ',
            )}
          >
            <DataSourceSection />
          </div>
        </div>
        <IntegrationFinalPopup />
        <NewProjectFinalPopup />
      </div>
    </PageContentWrapper>
  );
};

export default ControlDeskPage;
